import type { ChatModelRunOptions, ChatModelRunResult } from '@assistant-ui/react';
import { spaceApi, ApiError } from '../services/api';
import type {
  PlatformEvent,
  RunFinalAnswerDeltaData,
} from '../types/events';
import type { UIEvent } from '../types/ui-events';
import { extractMentions } from '../utils/directive-mentions';
import type { SubscribeFn } from '../contexts/PlatformEventContext';
import {
  createRunStream,
  pollRunStatus,
  mergeRunStreams,
  filterPrimary,
} from '../utils/run-event-stream';
import {
  getOrCreateConnection,
  DirectRunTimeoutError,
} from '../utils/direct-run-connection';
import type { StartRunResult } from '../utils/direct-run-connection';
import { getOrCreateIteration, stepsToIterations } from '../utils/content-builder';
import type { IterationState } from '../utils/content-builder';
import type { RunStepResponse } from '../types';
import type { AskUserAnswer } from '../types/inbox';
import {
  createRunStreamState,
  applyRunEvent,
  buildRunContent,
  type RunStreamState,
} from '../utils/run-stream-state';
import { playCue, playCueOnce } from './../utils/audio-theme';
import { eventDebug } from '../utils/event-debug';
import { isUserStopAbortReason } from '../utils/abort-reason';

export interface AsyncInvokeAdapterOptions {
  agentId: string;
  /** Resolves the active spaceId at invocation time. The adapter is created
   *  once per agent but re-reads spaceId on every invoke / cancel / status
   *  fetch via this getter so a stale render doesn't pin the wrong space. */
  getSpaceId: () => string | undefined;
  getConversationId: () => string | undefined;
  setConversationId: (id: string) => void;
  getUserTimezone: () => string;
  getSubscribe: () => SubscribeFn | undefined;
  onActiveRunId?: (runId: string | undefined) => void;
  dispatchUIEvent?: (event: UIEvent) => void;
  /** Resolves a tool name or database ID to a human-readable name (e.g. 'ui_event'). */
  getToolName?: (nameOrId: string) => string;
  /** Remove the last user+assistant message pair (used on cancel with no response). */
  removeLastExchange?: () => void;
  /** Put text back into the composer input — used on cancel so the user's
   *  message returns to the input for editing/resending after the exchange is
   *  removed from the thread. */
  restoreComposerText?: (text: string) => void;
  /** Awaits any in-flight composer attachment uploads, then returns the
   *  resulting FileRefs and clears the chip row. The adapter calls this
   *  right before `invokeAsync` so the agent never receives a fileRef
   *  pointing at a file that hasn't fully landed in the VFS yet. Optional —
   *  surfaces without composer attachments just don't pass it. */
  consumeAttachments?: () => Promise<import('../types').FileRef[]>;
}

// ─── Per-runId UI state store ────────────────────────────────────────────────
// Each in-flight or recently-completed run has an isolated slot keyed by
// runId. The CoT renderer subscribes to its message's runId, the
// context-utilization bar subscribes to the active runId, and conversation
// restore writes the last assistant message's runId. Concurrent runs cannot
// overwrite each other's UI state.
//
// Why: the previous design used four module-level singletons. A second run
// starting (or restoring) would clobber the slot that the first run's CoT
// was still reading, causing cross-run pollution.

export interface LiveToolCall {
  toolName: string;
  toolDisplayName?: string;
  toolCallId: string;
  input?: Record<string, unknown>;
  /** Backend-authored present-continuous CoT label (e.g. "Reading README.md"). */
  label?: string;
  hasResult: boolean;
}

export interface LiveIterationSnapshot {
  version: number;
  iterations: Array<{
    index: number;
    reasoningLen: number;
    responseLen: number;
    toolCalls: LiveToolCall[];
    isThinking: boolean | null;
    hasFinalAnswer: boolean;
  }>;
}

export interface RunProgress {
  hasFinalAnswer: boolean;
  /** True once the first final_answer_delta with content has been received. */
  hasTextContent: boolean;
  isRunComplete: boolean;
}

export interface RunContextUtilization {
  utilizationPercent: number | null;
  compactionThresholdPercent: number | null;
  compactionImminent: boolean;
  /** True while a context_compaction_start has fired but stop hasn't arrived yet */
  isCompacting: boolean;
}

export interface RunUiState {
  live: LiveIterationSnapshot;
  progress: RunProgress;
  durationMs: number | null;
  /** Wall-clock ms when this run's slot was first written — a stable timer
   *  origin so the "Thought for Xs" header reads the same elapsed regardless of
   *  when (or whether) a given header component mounts. */
  startedAt: number | null;
  contextUtilization: RunContextUtilization;
}

const EMPTY_RUN_STATE: RunUiState = {
  live: { version: 0, iterations: [] },
  progress: { hasFinalAnswer: false, hasTextContent: false, isRunComplete: false },
  durationMs: null,
  startedAt: null,
  contextUtilization: {
    utilizationPercent: null,
    compactionThresholdPercent: null,
    compactionImminent: false,
    isCompacting: false,
  },
};

class RunStateStore {
  private states = new Map<string, RunUiState>();
  private listenersByRunId = new Map<string, Set<() => void>>();
  private deleteTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /** Returns the current snapshot for a runId, or a stable empty snapshot for unknown ids. */
  getSnapshot(runId: string | undefined): RunUiState {
    if (!runId) return EMPTY_RUN_STATE;
    return this.states.get(runId) ?? EMPTY_RUN_STATE;
  }

  /** Subscribe to changes for a specific runId. No-op for undefined. */
  subscribe(runId: string | undefined, listener: () => void): () => void {
    if (!runId) return () => {};
    let set = this.listenersByRunId.get(runId);
    if (!set) {
      set = new Set();
      this.listenersByRunId.set(runId, set);
    }
    set.add(listener);
    return () => {
      const s = this.listenersByRunId.get(runId);
      if (!s) return;
      s.delete(listener);
      if (s.size === 0) this.listenersByRunId.delete(runId);
    };
  }

  private prev(runId: string): RunUiState {
    return this.states.get(runId) ?? EMPTY_RUN_STATE;
  }

  private notify(runId: string) {
    const set = this.listenersByRunId.get(runId);
    if (!set) return;
    for (const l of set) l();
  }

  updateLive(runId: string, iterations: Map<number, IterationState>) {
    const prev = this.prev(runId);
    const iters: LiveIterationSnapshot['iterations'] = [];
    for (const [num, iter] of iterations) {
      const tcs: LiveToolCall[] = [];
      for (const [, tc] of iter.toolCalls) {
        tcs.push({
          toolName: tc.toolName,
          toolDisplayName: tc.toolDisplayName,
          toolCallId: tc.toolCallId,
          input: tc.input,
          label: tc.label,
          hasResult: tc.output !== undefined || tc.error !== undefined,
        });
      }
      iters.push({
        index: num,
        reasoningLen: iter.reasoningText.length,
        responseLen: iter.responseText.length,
        toolCalls: tcs,
        isThinking: iter.isThinking,
        hasFinalAnswer: iter.hasFinalAnswer,
      });
    }
    this.states.set(runId, {
      ...prev,
      startedAt: prev.startedAt ?? Date.now(),
      live: { version: prev.live.version + 1, iterations: iters },
    });
    this.notify(runId);
  }

  updateProgress(runId: string, patch: Partial<RunProgress>) {
    const prev = this.prev(runId);
    this.states.set(runId, {
      ...prev,
      startedAt: prev.startedAt ?? Date.now(),
      progress: { ...prev.progress, ...patch },
    });
    this.notify(runId);
  }

  updateContextUtilization(runId: string, patch: Partial<RunContextUtilization>) {
    const prev = this.prev(runId);
    this.states.set(runId, {
      ...prev,
      contextUtilization: { ...prev.contextUtilization, ...patch },
    });
    this.notify(runId);
  }

  updateDuration(runId: string, durationMs: number | null) {
    const prev = this.prev(runId);
    this.states.set(runId, { ...prev, durationMs });
    this.notify(runId);
  }

  /** Remove a runId's slot. Future getSnapshot returns the empty stable snapshot. */
  delete(runId: string) {
    const timer = this.deleteTimers.get(runId);
    if (timer) {
      clearTimeout(timer);
      this.deleteTimers.delete(runId);
    }
    if (!this.states.has(runId)) return;
    this.states.delete(runId);
    this.notify(runId);
  }

  /** Schedule deletion after a delay so still-mounted CoT components keep reading. */
  scheduleDelete(runId: string, delayMs: number = 60_000): void {
    const existing = this.deleteTimers.get(runId);
    if (existing) clearTimeout(existing);
    this.deleteTimers.set(runId, setTimeout(() => this.delete(runId), delayMs));
  }

  /**
   * Clear a runId's slot at the START of a new run so a reused runId begins
   * fresh. The claude direct-run runtime reuses ONE runId for the whole warm
   * socket session (iterations accumulate across user turns), so without this a
   * follow-up turn would read the PRIOR turn's `isRunComplete: true` snapshot,
   * render "Thought for Ns", collapse, and freeze — ignoring the new turn's live
   * events. Also cancels any pending delete so the fresh state isn't reaped.
   * A previous turn's CoT that already collapsed has frozen its own snapshot, so
   * clearing the shared slot does not blank it.
   */
  reset(runId: string | undefined): void {
    if (!runId) return;
    const timer = this.deleteTimers.get(runId);
    if (timer) {
      clearTimeout(timer);
      this.deleteTimers.delete(runId);
    }
    if (!this.states.has(runId)) return;
    this.states.delete(runId);
    this.notify(runId);
  }
}

export const runStateStore = new RunStateStore();

// ─── Cancelled-run registry ──────────────────────────────────────────────────
// When the user hits Stop, the run keeps emitting events server-side (the
// backend finishes the in-flight step, then completes with "Run was
// cancelled."). Those events arrive on BOTH the direct socket and the fan-out
// subscription. Marking the runId here lets every consumer — the foreground
// adapter AND `useAgentRunEvents` (the background fan-out injector) — drop them,
// so a cancelled run never re-renders after we've torn down its exchange.
const cancelledRunIds = new Set<string>();

export function markRunCancelled(runId: string): void {
  if (!runId) return;
  cancelledRunIds.add(runId);
  // Evict eventually — the run is terminal server-side within seconds, after
  // which no more events arrive and the entry is dead weight.
  setTimeout(() => cancelledRunIds.delete(runId), 60_000);
}

export function isRunCancelled(runId: string | undefined): boolean {
  return !!runId && cancelledRunIds.has(runId);
}

// ─── Active run id (reactive observable) ─────────────────────────────────────
// Each surface's `useAgentRuntime` instantiates one of these to track its
// own foreground run. `useAgentRunEvents` subscribes to it so background-run
// thread.append calls can defer until the foreground stream finishes.
//
// The exported `activeFocusedRunIdStore` is a single shared observable that
// reflects whichever surface most-recently started/stopped a run; the
// context-utilization bar (which is a "current run" indicator and is only
// ever visible for one surface at a time) subscribes to this one.

export class ActiveRunIdStore {
  private value: string | undefined = undefined;
  private listeners = new Set<() => void>();

  get(): string | undefined { return this.value; }

  set(value: string | undefined) {
    if (this.value === value) return;
    this.value = value;
    for (const l of this.listeners) l();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  getSnapshot = (): string | undefined => this.value;
}

export const activeFocusedRunIdStore = new ActiveRunIdStore();

/**
 * Creates an async generator run function for the ChatModelAdapter.
 * Invokes the agent asynchronously and streams events via WebSocket.
 * Falls back to polling when the WebSocket subscribe function is unavailable.
 */
export function createAsyncInvokeRun(options: AsyncInvokeAdapterOptions) {
  const {
    agentId,
    getSpaceId,
    getConversationId,
    setConversationId,
    getUserTimezone,
    getSubscribe,
    onActiveRunId,
    dispatchUIEvent,
    getToolName,
    removeLastExchange,
    restoreComposerText,
    consumeAttachments,
  } = options;

  // Persist across runs so dismissed artifacts can be re-displayed.
  // Shared into each run's RunStreamState by reference, so the reducer's
  // tool_result handler writes into this map and buildRunContent reads
  // from it across the lifetime of the adapter.
  const artifactCache: RunStreamState['artifactCache'] = new Map();

  return async function* run({
    messages,
    abortSignal,
    runConfig,
  }: ChatModelRunOptions): AsyncGenerator<ChatModelRunResult> {
    // Resume mode: when the conversation reloads mid-run, `useResumeInFlightRun`
    // calls `thread.startRun({ runConfig: { custom: { resumeRunId } } })` to
    // re-enter this adapter for an already-executing run. We adopt that runId,
    // seed the reducer from its persisted steps, and stream the rest live —
    // skipping the invoke POST (the run is already going).
    const custom = runConfig?.custom as
      | { resumeRunId?: string; askUserRequestId?: string; answers?: AskUserAnswer[] }
      | undefined;
    const resumeRunId = custom?.resumeRunId;
    // Answer-over-socket: when the inline ask_user card has a live runtime
    // socket, it appends the rendered answer with these on `runConfig.custom`.
    // We forward them on the prompt frame so the backend resolves the request +
    // inbox item and continues the turn on this session (no REST `/answer`).
    const askUserRequestId = custom?.askUserRequestId;
    const askUserAnswers = custom?.answers;

    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
    const text =
      lastUserMessage?.content
        .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
        .map((p) => p.text)
        .join('\n') ?? '';

    // Hoisted to run() scope so the `finally` below (which runs on user-cancel
    // via generator `.return()`) can read them.
    let runId: string | undefined;
    let gotTerminal = false;

    // assistant-ui aborts the run generator for THREE distinct reasons (user
    // Stop, detach on navigation, and internal reason-less lifecycle aborts) —
    // see `abort-reason.ts`. ONLY the explicit user Stop may cancel the run
    // server-side: a detach must leave it running so `useResumeInFlightRun`
    // can re-attach on return, and an internal abort must never kill a healthy
    // run (a long reasoning block with no events yet is normal at high
    // reasoning effort — auto-cancelling there was the blank-screen RCA).
    const isUserStopAbort = () =>
      abortSignal.aborted && isUserStopAbortReason(abortSignal.reason);

    try {
      // Audio: fire the request cue at the moment the user's submission
      // hands off to the agent. Plays in parallel with the network call,
      // so the cue feels coincident with the message appearing.
      playCue('thread.request');

      // Yield immediately — shows CoT "Thinking..." before the network call
      yield {
        content: [{ type: 'reasoning' as const, text: '' }],
      } as ChatModelRunResult;

      // Composer text may carry inline directives:
      //   `:surface[…]{name=…}` (canvas, `#`)
      //   `:agent[…]{name=…}`   (delegation target, `@`)
      //   `:file[…]{name=…}`    (VFS file, `!`)
      // Lift them out into structured refs and send the agent the natural-
      // language version of the message with directive markup replaced.
      const {
        message: cleanMessage,
        surfaceRefs,
        agentRefs,
        fileRefs: mentionFileRefs,
      } = extractMentions(text);
      const invokeSpaceId = getSpaceId();
      if (!invokeSpaceId) {
        throw new ApiError('Cannot invoke agent: spaceId not yet resolved', 0);
      }
      const subscribe = getSubscribe();
      // Await any in-flight composer attachment uploads. User may have hit
      // Enter while a chip still showed progress; we hold the invoke until
      // every upload settles so the agent never receives a fileRef pointing
      // at a file that hasn't fully landed in the VFS yet.
      // A resumed run sends no new message, so it must not consume (and clear)
      // composer attachments — those belong to the next fresh send.
      const attachmentFileRefs = consumeAttachments && !resumeRunId ? await consumeAttachments() : [];
      // Merge attachment-derived and `!`-mention file refs, deduped by owner
      // + path. Attachments win on collision because they carry richer
      // metadata (size, contentType) than mentions, which only know path +
      // name. The owner is part of the key so a KB file and a conversation
      // file that happen to share a path (e.g. `report.md`) don't collide.
      const fileRefs = (() => {
        const seen = new Set<string>();
        const out = [];
        const key = (r: { kbId?: string; path: string }) => `${r.kbId ?? 'con'}::${r.path}`;
        for (const ref of attachmentFileRefs) {
          if (seen.has(key(ref))) continue;
          seen.add(key(ref));
          out.push(ref);
        }
        for (const ref of mentionFileRefs) {
          if (seen.has(key(ref))) continue;
          seen.add(key(ref));
          out.push(ref);
        }
        return out;
      })();

      // runId is assigned by whichever transport starts the run (direct socket
      // or REST fallback). The cancel handler reads it via the `cancelRun`
      // holder so a single abort listener covers both paths.
      let cancelRun: () => void = () => {};
      abortSignal.addEventListener('abort', () => {
        // Only a genuine Stop cancels server-side. Detach (navigation) and
        // internal assistant-ui aborts leave the run executing — the local
        // stream closes, but the runtime keeps working and the conversation
        // can re-attach to it later.
        if (!isUserStopAbort()) return;
        // Genuine Stop: mark the run cancelled so its still-arriving events are
        // ignored by both this adapter and the background fan-out injector.
        if (runId) markRunCancelled(runId);
        cancelRun();
      }, { once: true });

      const processEvents = async function*(
        stream: AsyncGenerator<PlatformEvent>,
        runId: string,
        seedSteps?: RunStepResponse[],
      ): AsyncGenerator<ChatModelRunResult> {
        // The reducer state — same shape used by useAgentRunEvents.
        // The artifactCache is shared by reference with the adapter so it
        // persists across runs (dismissed artifacts can be re-displayed).
        const state = createRunStreamState();
        state.artifactCache = artifactCache;

        // Resume seeding: rebuild the reducer from the run's persisted steps so
        // the trace that already happened before reload is on screen
        // immediately. Continuing live deltas append on top (WS is live-only,
        // so already-persisted deltas don't re-arrive); a late tool_result for a
        // seeded tool_call matches by toolCallId. Seeding every toolCallId into
        // dispatchedIds suppresses re-dispatch of ui_event canvas effects that
        // conversation-restore already applied (non-ui_event ids are inert there).
        if (seedSteps?.length) {
          for (const [k, v] of stepsToIterations(seedSteps)) state.iterations.set(k, v);
          const keys = [...state.iterations.keys()];
          state.currentIteration = keys.length ? Math.max(...keys) : 0;
          state.hasFinalAnswer = [...state.iterations.values()].some((it) => it.hasFinalAnswer);
          for (const s of seedSteps) if (s.toolCallId) state.dispatchedIds.add(s.toolCallId);
          runStateStore.updateLive(runId, state.iterations);
        }

        // Adapter-only: pendingTerminal carries assistant-ui's status
        // metadata across the queue-drain phase. The reducer just sets
        // state.terminal — we package it into pendingTerminal here.
        let pendingTerminal: ChatModelRunResult | null = null;

        const yieldContent = () => {
          // Publish live iteration state for the CoT component (bypasses aui reactive lag)
          runStateStore.updateLive(runId, state.iterations);
          return buildRunContent(state, { dispatchUIEvent, getToolName });
        };

        const yieldResult = (content: ReturnType<typeof yieldContent>): ChatModelRunResult =>
          ({ content, metadata: { custom: { runId } } } as ChatModelRunResult);

        for await (const event of stream) {
          if (abortSignal.aborted) break;

          // Side effects observed at event time — these affect adapter-only
          // run-scoped stores that the reducer doesn't know about.
          switch (event.eventType) {
            case 'agent.run.final_answer_start':
              // Fire the response cue as soon as the agent commits to a
              // final answer — playback (fetch + decode + start) runs in
              // parallel with the streamed text so the cue feels in sync
              // with the first words appearing on screen.
              playCueOnce('thread.response', `run:${runId}`);
              break;
            case 'agent.run.context_compaction_start':
              runStateStore.updateContextUtilization(runId, { isCompacting: true });
              break;
            case 'agent.run.context_compaction_stop':
              runStateStore.updateContextUtilization(runId, { isCompacting: false });
              break;
            case 'agent.run.tool_result': {
              if (process.env.NODE_ENV === 'development') {
                const artifacts = (event.data as { artifacts?: Array<{ artifactId: string; contentType: string; url?: string }> }).artifacts;
                if (artifacts) {
                  for (const ref of artifacts) {
                    console.log('[artifact-dispatch] cached artifact:', ref.artifactId, { contentType: ref.contentType, hasUrl: !!ref.url });
                  }
                }
              }
              break;
            }
            case 'agent.run.final_answer_delta': {
              if (process.env.NODE_ENV === 'development') {
                const data = event.data as unknown as RunFinalAnswerDeltaData;
                const iter = state.iterations.get(data.iteration);
                console.log('[adapter:final_answer_delta]', {
                  iteration: data.iteration,
                  accumulatedLen: iter?.finalAnswerText.length ?? 0,
                });
              }
              break;
            }
          }

          // Capture the faithful, deduped event sequence for replayable
          // fixtures (no-op unless event debug is enabled). This is the single
          // choke point that drives the CoT, so it matches what playback feeds.
          eventDebug.recordRunEventFull(runId, event);

          // Apply the event to the shared reducer.
          applyRunEvent(state, event, { dispatchUIEvent, getToolName });

          // Side effects after reducer — read from updated state.
          if (state.hasFinalAnswer) {
            runStateStore.updateProgress(runId, { hasFinalAnswer: true });
          }
          if (event.eventType === 'agent.run.final_answer_delta') {
            const data = event.data as unknown as RunFinalAnswerDeltaData;
            const iter = state.iterations.get(data.iteration);
            if (iter?.finalAnswerText) {
              runStateStore.updateProgress(runId, { hasTextContent: true });
            }
          }

          // Terminal handling — package state.terminal into pendingTerminal.
          if (state.terminal && !pendingTerminal) {
            const t = state.terminal;
            gotTerminal = true;
            runStateStore.updateProgress(runId, { isRunComplete: true });
            onActiveRunId?.(undefined);
            if (t.durationMs != null) {
              runStateStore.updateDuration(runId, t.durationMs);
            }

            if (t.kind === 'completed') {
              // Populate context utilization from completed event
              if (t.contextUtilization) {
                runStateStore.updateContextUtilization(runId, {
                  utilizationPercent: t.contextUtilization.utilizationPercent,
                  compactionThresholdPercent: t.contextUtilization.compactionThresholdPercent,
                  compactionImminent: t.contextUtilization.compactionImminent,
                });
              }
              runStateStore.updateContextUtilization(runId, { isCompacting: false });

              // Save authoritative response + metadata. Don't yield/return yet —
              // let the queue drain so late-arriving deltas/stop events are
              // incorporated before the final yield.
              pendingTerminal = {
                content: [],  // placeholder — rebuilt after drain
                status: { type: 'complete' as const, reason: 'stop' as const },
                metadata: {
                  custom: {
                    ...(t.durationMs != null && { runDurationMs: t.durationMs }),
                    ...(runId && { runId }),
                    ...(t.delegationRef && { delegationRef: t.delegationRef }),
                  },
                },
              } as ChatModelRunResult;

              // Yield intermediate update so the UI reflects completed state
              // while draining remaining events.
              yield yieldResult(yieldContent());
              continue;
            }

            if (t.kind === 'failed') {
              // Preserve existing iteration content so the CoT component
              // transitions cleanly instead of unmounting.
              const existingContent =
                state.iterations.size > 0 || state.subAgentGroups.length > 0
                  ? yieldContent()
                  : undefined;
              const errorPart = { type: 'text' as const, text: `Error: ${t.error}` };
              const errorContent = existingContent
                ? [...Array.from(existingContent), errorPart]
                : [errorPart];

              pendingTerminal = {
                content: errorContent,
                status: {
                  type: 'incomplete' as const,
                  reason: 'error' as const,
                  error: t.error,
                },
                metadata: {
                  custom: {
                    ...(t.durationMs != null && { runDurationMs: t.durationMs }),
                    ...(runId && { runId }),
                    ...(t.errorCode && { errorCode: t.errorCode }),
                    ...(t.delegationRef && { delegationRef: t.delegationRef }),
                  },
                },
              } as ChatModelRunResult;
              continue;
            }

            if (t.kind === 'cancelled') {
              // Note: a USER-initiated stop is handled centrally at the end of
              // run() (it reverts the exchange + restores the composer). This
              // branch only runs for a server-side cancellation that was
              // streamed through as a terminal — keep whatever the agent
              // produced and mark it cancelled.
              const rawContent =
                state.iterations.size > 0 || state.subAgentGroups.length > 0
                  ? yieldContent()
                  : undefined;
              const existingParts = rawContent ? [...rawContent] : [];
              const hasRealContent = existingParts.some(
                (p) =>
                  (p.type === 'text' &&
                    (p as { text?: string }).text &&
                    (p as { text?: string }).text!.length > 0) ||
                  p.type === 'tool-call',
              );

              if (hasRealContent) {
                pendingTerminal = {
                  content: [
                    ...existingParts,
                    { type: 'text' as const, text: '\n\n*Run cancelled.*' },
                  ],
                  status: { type: 'complete' as const, reason: 'stop' as const },
                  metadata: { custom: { ...(runId && { runId }), ...(t.delegationRef && { delegationRef: t.delegationRef }) } },
                } as ChatModelRunResult;
              } else {
                pendingTerminal = {
                  content: [],
                  status: { type: 'complete' as const, reason: 'stop' as const },
                } as ChatModelRunResult;
                queueMicrotask(() => removeLastExchange?.());
              }
              continue;
            }

            if (t.kind === 'incomplete') {
              // The agent ran out of iteration budget and never produced a
              // final answer, so `t.response` is a progress summary. This is a
              // PAUSE, not a failure: status stays `complete`/`stop` so the
              // composer re-enables and no error alert renders. The markers in
              // metadata drive the inline pause notice + Continue action.
              if (t.contextUtilization) {
                runStateStore.updateContextUtilization(runId, {
                  utilizationPercent: t.contextUtilization.utilizationPercent,
                  compactionThresholdPercent: t.contextUtilization.compactionThresholdPercent,
                  compactionImminent: t.contextUtilization.compactionImminent,
                });
              }
              runStateStore.updateContextUtilization(runId, { isCompacting: false });

              const existingContent =
                state.iterations.size > 0 || state.subAgentGroups.length > 0
                  ? yieldContent()
                  : undefined;
              const parts = existingContent ? [...Array.from(existingContent)] : [];
              // The summary only exists on the terminal — nothing streamed it as
              // answer text, so seed it or the turn renders as a blank bubble.
              const hasAnswerText = parts.some(
                (p) => p.type === 'text' && (p as { text?: string }).text?.trim(),
              );
              if (!hasAnswerText && t.response) {
                parts.push({ type: 'text' as const, text: t.response });
              }

              pendingTerminal = {
                content: parts,
                status: { type: 'complete' as const, reason: 'stop' as const },
                metadata: {
                  custom: {
                    ...(t.durationMs != null && { runDurationMs: t.durationMs }),
                    ...(runId && { runId }),
                    runIncomplete: true,
                    ...(t.stopReason && { stopReason: t.stopReason }),
                    ...(t.resumable && { resumable: true }),
                    ...(t.delegationRef && { delegationRef: t.delegationRef }),
                  },
                },
              } as ChatModelRunResult;
              yield yieldResult(yieldContent());
              continue;
            }

            if (t.kind === 'awaiting_input') {
              // ask_user ended the run while waiting for the human. Mark the
              // assistant message complete so the composer re-enables; keep the
              // streamed content (the inline ask_user card lives in the
              // iterations). The answer continues as a fresh follow-up run.
              const rawContent =
                state.iterations.size > 0 || state.subAgentGroups.length > 0
                  ? yieldContent()
                  : undefined;
              pendingTerminal = {
                content: rawContent ? [...rawContent] : [],
                status: { type: 'complete' as const, reason: 'stop' as const },
                metadata: {
                  custom: {
                    ...(t.durationMs != null && { runDurationMs: t.durationMs }),
                    ...(runId && { runId }),
                  },
                },
              } as ChatModelRunResult;
              yield yieldResult(yieldContent());
              continue;
            }
          }

          // Non-terminal event — yield intermediate content for streaming.
          yield yieldResult(yieldContent());
        }

        // If final_answer_start fired but final_answer_stop was lost (e.g. WS
        // delivered completed before the stop event and the grace period expired),
        // the delta-accumulated text may be truncated. Fetch the authoritative
        // response from the API as a fallback. Skipped for parked runs: their
        // run-record `response` is the QUESTION text, not an answer.
        if (
          state.hasFinalAnswer &&
          !state.receivedFinalAnswerStop &&
          runId &&
          state.terminal?.kind !== 'awaiting_input'
        ) {
          const fallbackSpaceId = getSpaceId();
          if (!fallbackSpaceId) {
            console.warn('[useAsyncInvokeAdapter] cannot fetch final answer fallback: spaceId missing');
          } else {
          try {
            const status = await spaceApi(fallbackSpaceId).runs.get(runId);
            if (status.response) {
              const lastIter = getOrCreateIteration(state.iterations, state.currentIteration);
              if (status.response.length > lastIter.finalAnswerText.length) {
                lastIter.finalAnswerText = status.response;
              }
            }
          } catch {
            // Best-effort — use whatever was accumulated from deltas
          }
          }
        }

        // After the queue is fully drained, yield the terminal result.
        // For completed runs, rebuild content to incorporate any late-arriving
        // deltas/stop events that arrived between completed and queue close.
        if (pendingTerminal) {
          if (pendingTerminal.status?.type === 'complete') {
            // Audio fallback for legacy runs that never emit
            // `final_answer_start` (older agents, polling-only paths).
            // Live runs fire the cue earlier on the start event;
            // playCueOnce dedupes by run id, so this is a no-op for them.
            if (state.terminal?.kind === 'completed' && state.hasFinalAnswer) {
              playCueOnce('thread.response', `run:${state.terminal.runId ?? runId}`);
            }
            // Parked (pending ask_user): the run legitimately ends with no
            // answer text — the question card IS the content. `pendingTerminal`
            // was frozen first-terminal-wins, so re-read state for metadata the
            // trailing parking `completed` absorbed after packaging.
            const parked = state.terminal?.kind === 'awaiting_input';
            if (parked && state.terminal?.durationMs != null) {
              runStateStore.updateDuration(runId!, state.terminal.durationMs);
              pendingTerminal = {
                ...pendingTerminal,
                metadata: {
                  ...pendingTerminal.metadata,
                  custom: {
                    ...(pendingTerminal.metadata?.custom ?? {}),
                    runDurationMs: state.terminal.durationMs,
                  },
                },
              } as ChatModelRunResult;
            }
            if (parked && state.terminal?.contextUtilization) {
              runStateStore.updateContextUtilization(runId!, {
                utilizationPercent: state.terminal.contextUtilization.utilizationPercent,
                compactionThresholdPercent:
                  state.terminal.contextUtilization.compactionThresholdPercent,
                compactionImminent: state.terminal.contextUtilization.compactionImminent,
              });
            }
            const content =
              state.iterations.size > 0 || state.subAgentGroups.length > 0
                ? yieldContent()
                : pendingTerminal.content;
            // Safety net: a run can complete without ever streaming answer
            // text (e.g. the response only exists on the run record). Fall
            // back to the terminal's authoritative response so the thread
            // never shows a silent blank message. Skipped for parked runs.
            const parts = Array.from(content ?? []);
            const hasAnswerText = parts.some(
              (p) => p.type === 'text' && (p as { text?: string }).text?.trim(),
            );
            const finalContent = hasAnswerText || parked
              ? content
              : [
                  ...parts,
                  {
                    type: 'text' as const,
                    text:
                      (state.terminal?.kind === 'completed' ||
                        state.terminal?.kind === 'incomplete') &&
                      state.terminal.response?.trim()
                        ? state.terminal.response
                        : '*The run finished without a response.*',
                  },
                ];
            yield {
              ...pendingTerminal,
              content: finalContent,
            } as ChatModelRunResult;
          } else {
            // Failed — content was already built with the error text
            yield pendingTerminal;
          }
        }
      };

      // ── Resume an already-executing run (page reloaded mid-run) ──────────
      // No invoke POST — the run is already going server-side. Seed from its
      // persisted steps and stream the remainder live, exactly like a fresh
      // run from this point on (same processEvents, same per-runId store slot).
      if (resumeRunId) {
        runId = resumeRunId;
        cancelRun = () => {
          const cancelSpaceId = getSpaceId();
          if (!cancelSpaceId) return;
          spaceApi(cancelSpaceId).runs.cancel(resumeRunId).catch(() => {/* best-effort */});
        };

        // Subscribe FIRST, buffering events, then fetch the snapshot in
        // parallel — so events that fire during runs.get aren't missed
        // (mirrors the REST fallback's early-subscribe pattern).
        const earlyBuffer: PlatformEvent[] = [];
        let earlyUnsub: (() => void) | undefined;
        if (subscribe) {
          const earlyCid = getConversationId();
          earlyUnsub = subscribe(
            [{ eventType: 'agent.run.*', level: 'full', ...(earlyCid ? { conversationId: earlyCid } : {}) }],
            (event) => earlyBuffer.push(event),
          );
        }

        let snapshot: Awaited<ReturnType<ReturnType<typeof spaceApi>['runs']['get']>> | undefined;
        try {
          snapshot = await spaceApi(invokeSpaceId).runs.get(resumeRunId);
        } catch (err) {
          earlyUnsub?.();
          eventDebug.log('resume', 'runs.get failed — cannot resume', {
            runId: resumeRunId,
            error: err instanceof Error ? err.message : String(err),
          });
          // Close the running bubble without dropping the persisted user turn.
          yield { content: [], status: { type: 'complete' as const, reason: 'stop' as const } } as ChatModelRunResult;
          return;
        }

        // Clear any stale slot BEFORE seeding (never after — it would clobber
        // the seed), then claim the active run so the background fan-out
        // injector defers to us and the context bar tracks this run.
        runStateStore.reset(resumeRunId);
        onActiveRunId?.(resumeRunId);
        eventDebug.recordRunStart(resumeRunId, eventDebug.getSnapshotState().wsConnected);
        eventDebug.setActiveRun(resumeRunId);

        const isTerminalSnapshot =
          !!snapshot && ['completed', 'failed', 'cancelled', 'error', 'incomplete'].includes(snapshot.status);

        if (isTerminalSnapshot) {
          // Race: the run finished between page load and our fetch. Render the
          // completed/failed bubble from the snapshot via pollRunStatus (which
          // emits the terminal event immediately — no 5s WS-inactivity wait).
          earlyUnsub?.();
          yield* processEvents(
            pollRunStatus(resumeRunId, abortSignal, getSpaceId),
            resumeRunId,
            snapshot!.steps,
          );
        } else {
          // Still running: seed from steps, stream live (with the early
          // buffer), then poll as a terminal backstop.
          if (subscribe) {
            earlyUnsub?.();
            yield* processEvents(
              createRunStream(subscribe, resumeRunId, abortSignal, {
                includeSubagents: true,
                preBufferedEvents: earlyBuffer,
                getSpaceId,
              }),
              resumeRunId,
              snapshot?.steps,
            );
          } else {
            earlyUnsub?.();
          }
          if (!gotTerminal && !abortSignal.aborted) {
            yield* processEvents(
              pollRunStatus(resumeRunId, abortSignal, getSpaceId),
              resumeRunId,
              snapshot?.steps,
            );
          }
          if (!gotTerminal && !abortSignal.aborted) {
            // WS + poll both ended without a terminal — close the bubble but
            // KEEP the exchange (unlike a fresh send, the user turn is persisted).
            yield { content: [], status: { type: 'complete' as const, reason: 'stop' as const } } as ChatModelRunResult;
          }
        }
        return;
      }

      // ── Transport selection ──────────────────────────────────────────────
      // Preferred path: a direct, per-run WebSocket to the conversation's warm
      // micro-VM (low latency, prompt sent over the socket). Fan-out remains
      // open for sub-agent / ask_user / delegated events. On any pre-run-start
      // failure we fall back to today's REST invoke-async + fan-out path.
      let direct: StartRunResult | null = null;
      let directError: unknown;
      try {
        // Create-first: a brand-new conversation needs an id before we can
        // presign the conversation-scoped ticket.
        let cid = getConversationId();
        if (!cid) {
          const created = await spaceApi(invokeSpaceId).conversations.create({ agentId });
          cid = created.conversationId;
          setConversationId(cid);
        }
        const conn = getOrCreateConnection(cid, { getSpaceId, agentId });
        direct = await conn.startRun({
          prompt: cleanMessage,
          surfaceRefs: surfaceRefs.length ? surfaceRefs : undefined,
          agentRefs: agentRefs.length ? agentRefs : undefined,
          attachments: fileRefs.length ? fileRefs : undefined,
          askUserRequestId,
          answers: askUserAnswers,
          signal: abortSignal,
        });
        runId = direct.runId;
        cancelRun = () => conn.cancel(direct!.runId);
        eventDebug.log('direct-ws', 'run started over direct socket', { runId });
      } catch (err) {
        directError = err;
        eventDebug.log('direct-ws', 'presign/handshake failed — falling back to REST', {
          error: err instanceof Error ? err.message : String(err),
        });
        direct = null;
      }

      if (direct) {
        const activeRunId = direct.runId;
        // Reused runId (warm claude socket → one runId across turns): clear any
        // prior turn's completed snapshot so THIS turn's CoT starts fresh and
        // streams instead of freezing on the old "Thought for Ns".
        runStateStore.reset(activeRunId);
        setConversationId(direct.conversationId);
        onActiveRunId?.(activeRunId);
        eventDebug.recordRunStart(activeRunId, eventDebug.getSnapshotState().wsConnected);
        eventDebug.setActiveRun(activeRunId);

        // Primary run streams from the socket (lead). Fan-out supplies
        // sub-agent / ask_user / delegated events; its copies of the socket-
        // owned primary events are filtered out to avoid double-rendering.
        if (subscribe) {
          const fanout = filterPrimary(
            createRunStream(subscribe, activeRunId, abortSignal, {
              includeSubagents: true,
              getSpaceId,
            }),
            activeRunId,
          );
          yield* processEvents(mergeRunStreams(direct.stream, fanout), activeRunId);
        } else {
          yield* processEvents(direct.stream, activeRunId);
        }

        // The socket ended without a terminal (dropped mid-run) → recover the
        // SAME run via the unfiltered fan-out path, then poll. No re-invoke.
        if (!gotTerminal && !abortSignal.aborted && subscribe) {
          yield* processEvents(
            createRunStream(subscribe, activeRunId, abortSignal, {
              includeSubagents: true,
              getSpaceId,
            }),
            activeRunId,
          );
        }
        if (!gotTerminal && !abortSignal.aborted) {
          yield* processEvents(pollRunStatus(activeRunId, abortSignal, getSpaceId), activeRunId);
        }
      } else if (directError instanceof DirectRunTimeoutError) {
        // The socket connected and the prompt was SENT, but no run started in
        // time. A run may already be executing server-side, so falling back to
        // REST would double-execute it (the bug that ran two agents in
        // parallel). Surface the error instead — for BOTH runtimes.
        throw new Error(
          'The agent runtime did not start the run in time. Please try again.',
        );
      } else {
        // ── REST fallback ───────────────────────────────────────────────────
        // Reached only when the direct socket could NOT be established (presign
        // / socket open failed, or the backend rejected the prompt pre-run) — so
        // no run is running and a REST invoke is safe. Same policy for both
        // runtimes: WS first, REST only when the WS path is genuinely
        // unavailable. (Note: invoke-async runs the AgentCore runtime, so a
        // claude agent's REST fallback answer comes from AgentCore.)
        //
        // Subscribe early to capture WS events that arrive during invokeAsync()
        // (runId isn't known yet); scope by conversationId when we have one.
        const earlyBuffer: PlatformEvent[] = [];
        let earlyUnsub: (() => void) | undefined;
        if (subscribe) {
          const earlyCid = getConversationId();
          earlyUnsub = subscribe(
            [{ eventType: 'agent.run.*', level: 'full', ...(earlyCid ? { conversationId: earlyCid } : {}) }],
            (event) => earlyBuffer.push(event),
          );
        }

        const invoked = await spaceApi(invokeSpaceId).agents.invokeAsync(agentId, {
          message: cleanMessage,
          conversationId: getConversationId(),
          context: { timezone: getUserTimezone() },
          ...(surfaceRefs.length > 0 && { surfaceRefs }),
          ...(agentRefs.length > 0 && { agentRefs }),
          ...(fileRefs.length > 0 && { fileRefs }),
          // Answer-over-socket that fell back to REST (presign/handshake
          // failed): still carry the ask ids so invoke-async runs the ask_user
          // closeout — otherwise the answer degrades to a plain message and
          // the request is never resolved.
          ...(askUserRequestId &&
            askUserAnswers && { askUserRequestId, answers: askUserAnswers }),
        });
        earlyUnsub?.();

        const activeRunId = invoked.runId;
        runId = activeRunId;
        // Reused runId: clear any prior turn's completed snapshot (see direct path).
        runStateStore.reset(activeRunId);
        setConversationId(invoked.conversationId);
        onActiveRunId?.(activeRunId);
        eventDebug.recordRunStart(activeRunId, eventDebug.getSnapshotState().wsConnected);
        eventDebug.setActiveRun(activeRunId);
        cancelRun = () => {
          const cancelSpaceId = getSpaceId();
          if (!cancelSpaceId) return;
          spaceApi(cancelSpaceId).runs.cancel(activeRunId).catch(() => {/* best-effort */});
        };

        if (subscribe) {
          yield* processEvents(
            createRunStream(subscribe, activeRunId, abortSignal, {
              includeSubagents: true,
              preBufferedEvents: earlyBuffer,
              getSpaceId,
            }),
            activeRunId,
          );
        }
        if (!gotTerminal && !abortSignal.aborted) {
          yield* processEvents(pollRunStatus(activeRunId, abortSignal, getSpaceId), activeRunId);
        }
      }

      // Stream exhausted without a terminal and NOT user-cancelled (WS + poll
      // both ended). Yield a terminal status so assistant-ui exits "Thinking…"
      // and drop the empty exchange. (The user-cancel case is handled in the
      // `finally` below — see the note there.)
      if (!gotTerminal && !abortSignal.aborted) {
        yield {
          content: [],
          status: { type: 'complete' as const, reason: 'stop' as const },
        } as ChatModelRunResult;
        queueMicrotask(() => removeLastExchange?.());
      }
    } catch (err) {
      // The runId may or may not be assigned (invokeAsync may have failed
      // before returning). assistant-ui's message.status carries the
      // terminal state to the CoT component regardless, so we don't need
      // to write to runStateStore here.
      const message =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : err instanceof Error
            ? err.message
            : 'An unknown error occurred';
      yield {
        content: [{ type: 'text' as const, text: `Error: ${message}` }],
        status: {
          type: 'incomplete' as const,
          reason: 'error' as const,
          error: message,
        },
      } as ChatModelRunResult;
    } finally {
      // Runs on normal completion, error, AND user cancel. Cancel is the
      // important case: assistant-ui `break`s its consumer loop on Stop, which
      // calls `.return()` on this generator — so code after the current `yield`
      // never executes, but `finally` blocks DO. So the cancel UX lives here.
      if (runId) {
        runStateStore.updateProgress(runId, { isRunComplete: true });
        // Reclaim the per-run UI state slot after a generous window (CoT stays
        // mounted on the message and reads the frozen final snapshot meanwhile).
        runStateStore.scheduleDelete(runId);
      }
      onActiveRunId?.(undefined);
      eventDebug.setActiveRun(undefined);

      // User hit Stop: drop the whole exchange (agent output + user message)
      // and hand the text back to the composer for editing/resending. The run
      // is already marked cancelled (abort listener), so its trailing events
      // are ignored by both this adapter and the background fan-out injector.
      // A detach abort (navigation) or an internal assistant-ui abort is NOT a
      // Stop — the run lives on and must keep its exchange so the resumed
      // conversation shows it.
      if (isUserStopAbort()) {
        queueMicrotask(() => {
          removeLastExchange?.();
          restoreComposerText?.(text);
        });
      }
    }
  };
}
